import clsx from 'clsx';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'react-bootstrap-icons';

import { DataTestIds } from '@repo/frontend-shared';

import { SDSButton } from '../SDSButton/SDSButton';
import { SDSFlexbox } from '../SDSFlexbox/SDSFlexbox';
import { SDSIconButton } from '../SDSIconButton/SDSIconButton';
import { SDSMenu, SDSMenuItem } from '../SDSMenu/SDSMenu';
import { SDSText } from '../SDSText/SDSText';
import { buildVisiblePages } from './sds-pagination-utils';

import './sds-pagination.scss';

export interface SDSPaginationProps {
  /** Zero-based index of the page on screen. */
  pageIndex: number;
  pageCount: number;
  /**
   * Pages that can actually be opened, when that is fewer than exist.
   *
   * A keyset-paged list only knows the cursor for pages it has walked to, so
   * the numbers beyond that are shown — the reader is entitled to know how
   * much there is — but not offered. Defaults to every page being reachable.
   */
  reachablePageCount?: number;
  /** Rendered as "Total: n" when given. Omitted entirely when the count is unknown. */
  totalCount?: number;
  /** Rows per page. Renders the chooser only when the options are given too. */
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onChangePageSize?(size: number): void;
  isDisabled?: boolean;
  onGoToPage(index: number): void;
  /** An extra control after the page-size chooser. */
  trailing?: ReactNode;
  className?: string;
  dataTestId?: string;
}

function PageButton({
  index,
  isCurrent,
  isDisabled,
  onGoToPage,
}: {
  index: number;
  isCurrent: boolean;
  isDisabled: boolean;
  onGoToPage(index: number): void;
}) {
  return (
    <SDSButton
      className={clsx('SDSPagination__page', isCurrent && 'SDSPagination__page--current')}
      text={String(index + 1)}
      textType="body-large-bold"
      mode="ghost"
      size="small"
      isDisabled={isDisabled}
      ariaLabel={`Page ${index + 1}`}
      ariaCurrent={isCurrent ? 'page' : undefined}
      onClick={() => onGoToPage(index)}
      dataTestId={DataTestIds.SDS.SDS_PAGINATION_PAGE}
    />
  );
}

function PageSizeChooser({
  pageSize,
  pageSizeOptions,
  isDisabled,
  onChangePageSize,
}: {
  pageSize: number;
  pageSizeOptions: readonly number[];
  isDisabled: boolean;
  onChangePageSize(size: number): void;
}) {
  return (
    <SDSMenu
      dataTestId={DataTestIds.SDS.SDS_PAGINATION_PAGE_SIZE_MENU}
      triggerClassName="SDSPagination__page-size"
      // The trigger is already a button, so the label and chevron go inside it
      // as plain content. Nesting an SDSButton here would put one button
      // inside another — invalid markup, and the disabled state would sit on
      // the inner one while the outer kept opening the menu.
      triggerProps={{
        disabled: isDisabled,
        'aria-label': `Rows per page, currently ${pageSize}`,
        'data-testid': DataTestIds.SDS.SDS_PAGINATION_PAGE_SIZE,
      }}
      trigger={
        <>
          <SDSText type="body-large-bold" text={`${pageSize} on page`} color="inherit" />
          <ChevronDown aria-hidden />
        </>
      }
    >
      {pageSizeOptions.map(size => (
        <SDSMenuItem key={size} onClick={() => onChangePageSize(size)}>
          {`${size} on page`}
        </SDSMenuItem>
      ))}
    </SDSMenu>
  );
}

export function SDSPagination({
  pageIndex,
  pageCount,
  reachablePageCount,
  totalCount,
  pageSize,
  pageSizeOptions,
  onChangePageSize,
  isDisabled = false,
  onGoToPage,
  trailing,
  className,
  dataTestId = DataTestIds.SDS.SDS_PAGINATION,
}: SDSPaginationProps) {
  const reachable = reachablePageCount ?? pageCount;
  const visiblePages = buildVisiblePages(pageCount, pageIndex);
  const showsPageSize = pageSize !== undefined && pageSizeOptions !== undefined && onChangePageSize !== undefined;
  // A single page keeps its controls, inert: the bar holds its shape across
  // lists of any size, and a number that cannot be clicked is a clearer
  // statement that there is nowhere to go than an empty space would be.
  const hasNowhereToGo = pageCount <= 1;

  return (
    <SDSFlexbox
      className={clsx('SDSPagination', className)}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap="text-x-large"
      wrap="wrap"
      fullWidth
      dataTestId={dataTestId}
    >
      {/* Nothing rather than zero when the count is unknown: a caller that
          cannot count its set must not appear to claim it is empty. */}
      {totalCount === undefined ? (
        <span className="SDSPagination__spacer" />
      ) : (
        <SDSText
          type="body-large-bold"
          text={`Total: ${totalCount}`}
          color="inactive"
          dataTestId={DataTestIds.SDS.SDS_PAGINATION_TOTAL}
        />
      )}

      <SDSFlexbox direction="row" alignItems="center" gap="text-x-large" wrap="wrap">
        <SDSIconButton
          className="SDSPagination__arrow"
          icon={<ChevronLeft />}
          size="small"
          ariaLabel="Previous page"
          isDisabled={isDisabled || pageIndex === 0}
          onClick={() => onGoToPage(pageIndex - 1)}
          dataTestId={DataTestIds.SDS.SDS_PAGINATION_PREVIOUS}
        />

        <SDSFlexbox direction="row" alignItems="center" gap="button-regular">
          {visiblePages.map(index => (
            <PageButton
              key={index}
              index={index}
              isCurrent={index === pageIndex}
              isDisabled={isDisabled || hasNowhereToGo || index >= reachable}
              onGoToPage={onGoToPage}
            />
          ))}
        </SDSFlexbox>

        <SDSIconButton
          className="SDSPagination__arrow"
          icon={<ChevronRight />}
          size="small"
          ariaLabel="Next page"
          isDisabled={isDisabled || pageIndex + 1 >= reachable}
          onClick={() => onGoToPage(pageIndex + 1)}
          dataTestId={DataTestIds.SDS.SDS_PAGINATION_NEXT}
        />

        {showsPageSize ? (
          <PageSizeChooser
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            isDisabled={isDisabled}
            onChangePageSize={onChangePageSize}
          />
        ) : null}

        {trailing}
      </SDSFlexbox>
    </SDSFlexbox>
  );
}
